import { isUuid, maybeSlugToUuid } from '#pplx/frontend/apps/ask/permalink-slug/permalinkSlug.js';
/** Shell prefixes that serve the standard permalink routes. */
export const LEGACY_PERMALINK_PATH_PREFIXES = [
    '/app',
    '/computer-app',
    '/embedded-sidecar',
    '/inline-assistant',
    '/mobile-sidecar',
    '/sidecar',
];
const STANDARD_PERMALINK_PATH_PATTERN = new RegExp('^' +
    `(?:${LEGACY_PERMALINK_PATH_PREFIXES.join('|')})?` +
    '/(?:' +
    'search(?:/new)?' +
    '|session' +
    '|page' +
    '|computer/(?:a|t|tasks|gallery|live)' +
    ')' +
    '/([^/]+)$');
const COLLECTION_PERMALINK_PATH_PATTERN = new RegExp('^' +
    `(?:${LEGACY_PERMALINK_PATH_PREFIXES.join('|')})?` +
    '/(?:collections|projects|spaces)' +
    '/([^/]+)' +
    '(?:/([^/]+))?' +
    '$');
const PROJECT_THREAD_PERMALINK_PATH_PATTERN = /^((?:\/projects\/[^/]+)\/sessions\/)([^/]+)(\/.*)?$/;
/** Return the UUID-form path for a legacy permalink, or null to leave it alone. */
export function canonicalPermalinkPath(pathname) {
    const trailingSlash = pathname.length > 1 && pathname.endsWith('/') ? '/' : '';
    const path = trailingSlash ? pathname.slice(0, -1) : pathname;
    const collectionMatch = COLLECTION_PERMALINK_PATH_PATTERN.exec(path);
    if (collectionMatch !== null) {
        const [, collectionSegment, threadSegment] = collectionMatch;
        if (collectionSegment === undefined) {
            return null;
        }
        const canonicalCollection = canonicalLegacySegment(collectionSegment);
        const canonicalThread = threadSegment === undefined ? null : canonicalLegacySegment(threadSegment);
        if (canonicalCollection === null && canonicalThread === null) {
            return null;
        }
        const originalSuffix = threadSegment === undefined ? collectionSegment : `${collectionSegment}/${threadSegment}`;
        const canonicalSuffix = threadSegment === undefined
            ? (canonicalCollection ?? collectionSegment)
            : `${canonicalCollection ?? collectionSegment}/${canonicalThread ?? threadSegment}`;
        return path.slice(0, -originalSuffix.length) + canonicalSuffix + trailingSlash;
    }
    const standardSegment = STANDARD_PERMALINK_PATH_PATTERN.exec(path)?.[1];
    const projectThreadMatch = PROJECT_THREAD_PERMALINK_PATH_PATTERN.exec(path);
    const segment = standardSegment ?? projectThreadMatch?.[2];
    if (segment === undefined) {
        return null;
    }
    const uuid = canonicalLegacySegment(segment);
    if (uuid === null)
        return null;
    if (projectThreadMatch) {
        const [, prefix, , suffix = ''] = projectThreadMatch;
        return `${prefix}${uuid}${suffix}${trailingSlash}`;
    }
    return path.slice(0, -segment.length) + uuid + trailingSlash;
}
/** Return a UUID-form path when the input is a recognized legacy permalink. */
export function canonicalizeLegacyPermalinkPath(pathname) {
    return canonicalPermalinkPath(pathname) ?? pathname;
}
function canonicalLegacySegment(segment) {
    if (isUuid(segment) || !segment.includes('-')) {
        return null;
    }
    return maybeSlugToUuid(segment);
}
/** Replace a legacy browser URL in place without navigating or reloading. */
export function canonicalizeLegacyPermalinkUrl(location, history) {
    const canonicalPath = canonicalPermalinkPath(location.pathname);
    if (canonicalPath === null) {
        return;
    }
    try {
        history.replaceState(history.state, '', canonicalPath + location.search + location.hash);
    }
    catch {
        // This may run before observability initializes. A browser history failure
        // must not prevent the application or its error reporting from starting.
    }
}
/** Remove a legacy title from an absolute or root-relative URL. */
export function canonicalizeLegacyPermalinkUrlText(value) {
    try {
        if (value.startsWith('/') && !value.startsWith('//')) {
            const url = new URL(value, 'https://relative.invalid');
            const canonicalPath = canonicalPermalinkPath(url.pathname);
            return canonicalPath === null ? value : canonicalPath + url.search + url.hash;
        }
        const url = new URL(value);
        const canonicalPath = canonicalPermalinkPath(url.pathname);
        if (canonicalPath === null) {
            return value;
        }
        url.pathname = canonicalPath;
        return url.href;
    }
    catch {
        return value;
    }
}
/** Canonicalize SPA destinations before URL observers such as RUM receive them. */
export function installLegacyPermalinkHistoryCanonicalizer() {
    window.history.pushState = wrapHistoryMethod(window.history.pushState);
    window.history.replaceState = wrapHistoryMethod(window.history.replaceState);
}
function wrapHistoryMethod(method) {
    return function (data, unused, destination) {
        method.call(this, data, unused, canonicalHistoryDestination(destination, window.location.href));
    };
}
function canonicalHistoryDestination(destination, currentUrl) {
    if (destination === null || destination === undefined) {
        return destination;
    }
    try {
        const url = new URL(destination, currentUrl);
        const canonicalPath = canonicalPermalinkPath(url.pathname);
        if (canonicalPath === null) {
            return destination;
        }
        url.pathname = canonicalPath;
        return url;
    }
    catch {
        return destination;
    }
}
//# sourceMappingURL=canonicalizeLegacyPermalink.js.map